import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSelector } from 'react-redux';
import { selectAlertsStatus } from '../store/slices/alertsSlice';
import { getGlobalIntervalManager } from '../utils/useAlertsAutoRefresh';

const REFRESH_INTERVAL = 30000; // 30 seconds - should match the one in useAlertsAutoRefresh

interface GlobalAlertTimerProps {
  onManualRefresh?: () => void;
}

const GlobalAlertTimer: React.FC<GlobalAlertTimerProps> = ({ onManualRefresh }) => {
  const [timeUntilRefresh, setTimeUntilRefresh] = useState<number>(REFRESH_INTERVAL);
  const alertsStatus = useSelector(selectAlertsStatus);
  const globalManager = getGlobalIntervalManager();

  const formatTime = (milliseconds: number): string => {
    const seconds = Math.ceil(milliseconds / 1000);
    return `${seconds}s`;
  };

  const isNearRefresh = timeUntilRefresh <= 5000; // Last 5 seconds
  const isLoading = alertsStatus === 'loading' || globalManager.isCurrentlyRefreshing();

  useEffect(() => {
    const updateTimer = () => {
      const remaining = globalManager.getTimeUntilRefresh();
      setTimeUntilRefresh(remaining);

      // Debug logging for stuck timer issues (only in development)
      if (import.meta.env.DEV && remaining === 0 && !isLoading) {
        console.debug('Alert timer at 0s but not loading. Last refresh:', new Date(globalManager.getLastRefreshTime()));
      }
    };

    // Update immediately
    updateTimer();

    // Set up regular updates
    const timer = setInterval(updateTimer, 100); // Update every 100ms for smooth countdown

    return () => clearInterval(timer);
  }, [globalManager, isLoading]);

  return (
    <Tooltip
      title={
        isLoading
          ? 'Refreshing alerts...'
          : timeUntilRefresh === 0
            ? 'Refreshing alerts soon...'
            : `Next alert refresh in ${formatTime(timeUntilRefresh)}`
      }
      arrow
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: onManualRefresh ? 'pointer' : 'default',
          padding: '4px 8px',
          borderRadius: '16px',
          backgroundColor: alpha('#ffffff', 0.1),
          border: `1px solid ${alpha('#ffffff', 0.2)}`,
          transition: 'all 0.3s ease',
          '&:hover': onManualRefresh
            ? {
                backgroundColor: alpha('#ffffff', 0.15),
                borderColor: alpha('#ffffff', 0.3),
              }
            : {},
        }}
        onClick={onManualRefresh}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: isLoading ? '#4CAF50' : isNearRefresh ? '#FF9800' : '#ffffff',
            transition: 'color 0.3s ease',
          }}
        >
          {isLoading ? (
            <RefreshIcon
              sx={{
                fontSize: 16,
                mr: 0.5,
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': {
                    transform: 'rotate(0deg)',
                  },
                  '100%': {
                    transform: 'rotate(360deg)',
                  },
                },
              }}
            />
          ) : (
            <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5 }} />
          )}
          <Typography
            variant='caption'
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
              minWidth: '20px',
              textAlign: 'center',
            }}
          >
            {isLoading ? 'Refreshing' : timeUntilRefresh === 0 ? 'Soon' : formatTime(timeUntilRefresh)}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default GlobalAlertTimer;
